// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import DashboardRefresh from '@Organisms/Dashboard/DashboardPanel/DashboardRefresh/DashboardRefresh';
import DashboardPeriod from '@Organisms/Dashboard/DashboardPanel/DashboardPeriod/DashboardPeriod';
import DashboardConfig from '@Organisms/Dashboard/DashboardConfig/DashboardConfig';
import { useLocalMessage, downloadFile } from '@Helpers/';
import { useDashboard } from '@Hooks/useDashboard';
import DashboardExport from '@Organisms/Dashboard/DashboardExport/DashboardExport';
import { DashboardEventList } from '@Organisms/Dashboard/eventList';
import { ICONS } from '@Constants/iconsApp';
import { HeaderButton } from '@Atoms/Buttons';
import * as htmlToImage from 'html-to-image';
import { useGetSettings } from '@Queries/UISettingsQueries';
import { DashboardCache } from './DashboardCache/DashboardCache';

import './DashboardPanel.scss';

interface DashboardPanelProps {
    eventList: DashboardEventList;
    refetch: () => void;
}

const DashboardPanel = ({ eventList, refetch }: DashboardPanelProps): ReactElement => {
    const i = useLocalMessage();
    const { data: settingsData } = useGetSettings();
    const {
        disallowRefreshInDashboards = false,
        disallowPeriodAllInDashboards = false,
        disableCacheOption = false,
    } = settingsData || {};

    const { dashboard, editMode, setEditMode } = useDashboard();

    const handleExit = (): void => {
        setEditMode(false);
        refetch();
    };

    const handleSave = (): void => {
        eventList.onEdit({ definition: dashboard.definition }, dashboard._id);
        setEditMode(false);
    };

    const ActionTHButtons = editMode ? (
        <>
            <HeaderButton label={i('app.general.save')} onClick={handleSave} icon={<ICONS.SaveOutlined />} />
            <HeaderButton label={i('general.cancel')} onClick={handleExit} icon={<ICONS.CloseIcon />} />
        </>
    ) : (
        <HeaderButton label={i('general.edit')} onClick={() => setEditMode(true)} icon={<ICONS.EditIcon />} />
    );

    return (
        <div className="dashboard-panel">
            <div className="dashboard-panel__header">
                <div className="dashboard-panel__title">
                    {!disallowRefreshInDashboards && <DashboardRefresh dashboard={dashboard} />}
                    <DashboardPeriod
                        dashboard={dashboard}
                        eventList={eventList}
                        allowPeriodAll={disallowPeriodAllInDashboards}
                    />
                    {!disableCacheOption && <DashboardCache dashboard={dashboard} eventList={eventList} />}
                </div>

                <div className="dashboard-panel__actions">
                    {dashboard.writable && ActionTHButtons}

                    <DashboardExport dashboard={dashboard} />

                    <HeaderButton
                        label={i('dashboard.item.export')}
                        onClick={() => {
                            const element = document.getElementById('dashboard-view-content');

                            if (!element) {
                                return;
                            }

                            htmlToImage
                                .toPng(element, { cacheBust: true })
                                .then((file) => downloadFile(dashboard.title || 'widget', file, 'image/png', true));
                        }}
                        icon={<ICONS.FileImageOutlined />}
                    />
                </div>
            </div>

            {editMode && (
                <div className="dashoard-config-container">
                    <DashboardConfig />
                </div>
            )}
        </div>
    );
};

export default DashboardPanel;
